import { useCallback, useEffect, useState } from 'react';
import { useUserStore, useUIStore } from '../store';
import { usePaymentService, type UserAccessData } from '../services/paymentService';
import { getUserId } from '../utils/userId';
import type { SubscriptionPlan } from '../types';

interface UseProAccessReturn {
  isProUser: boolean;
  isPremiumUser: boolean;
  hasProAccess: boolean;
  hasPremiumAccess: boolean;
  currentPlan: SubscriptionPlan;
  
  // Loading state
  isLoading: boolean;
  
  // User data
  userId: string;
  accessData: UserAccessData | null;
  
  // Feature access checks
  canUseFeature: (featureTier: 'pro' | 'premium') => boolean;
  requiresUpgrade: (featureTier: 'pro' | 'premium') => boolean;
  hasSpecificFeature: (featureName: keyof UserAccessData['features']) => boolean;
  
  // UI helpers
  showUpgradePrompt: (featureName: string, requiredTier: 'pro' | 'premium') => void;
  getFeatureAccessMessage: (featureTier: 'pro' | 'premium') => string;
  
  // Plan information
  getPlanDisplayName: (plan?: SubscriptionPlan) => string;
  getPlanPrice: (plan: SubscriptionPlan) => string;
  getPlanFeatures: (plan: SubscriptionPlan) => string[];
  
  // Actions
  refreshAccess: () => Promise<void>;
  syncAccess: () => Promise<void>;
}

export const useProAccess = (): UseProAccessReturn => {
  const { addNotification } = useUIStore();
  const { getUserAccess, refreshUserAccess, syncUserAccess } = usePaymentService();
  
  const [isLoading, setIsLoading] = useState(true);
  const [accessData, setAccessData] = useState<UserAccessData | null>(null);
  
  const userId = getUserId();

  // 初始加载用户权限数据
  useEffect(() => {
    const loadUserAccess = async () => {
      try {
        setIsLoading(true);
        const data = await getUserAccess();
        setAccessData(data);
      } catch (error) {
        console.error('加载用户权限失败:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAccess();
  }, [getUserAccess]);

  // 计算用户状态
  const currentPlan: SubscriptionPlan = accessData?.accessLevel || 'free';
  const isProUser = accessData?.hasProAccess || false;
  const isPremiumUser = accessData?.hasPremiumAccess || false;
  const hasProAccess = isProUser;
  const hasPremiumAccess = isPremiumUser;

  /**
   * Check if user can use a specific feature tier
   */
  const canUseFeature = useCallback((featureTier: 'pro' | 'premium'): boolean => {
    if (!accessData) return false;
    
    if (featureTier === 'pro') {
      return accessData.hasProAccess || accessData.hasPremiumAccess;
    }
    
    if (featureTier === 'premium') {
      return accessData.hasPremiumAccess;
    }
    
    return false;
  }, [accessData]);

  /**
   * Check if user needs to upgrade for a feature
   */
  const requiresUpgrade = useCallback((featureTier: 'pro' | 'premium'): boolean => {
    return !canUseFeature(featureTier);
  }, [canUseFeature]);

  /**
   * Check if user has access to a specific feature
   */
  const hasSpecificFeature = useCallback((featureName: keyof UserAccessData['features']): boolean => {
    if (!accessData) return false;
    return accessData.features[featureName];
  }, [accessData]);

  /**
   * Show upgrade prompt notification
   */
  const showUpgradePrompt = useCallback((featureName: string, requiredTier: 'pro' | 'premium') => {
    const tierName = requiredTier === 'pro' ? 'Pro' : 'Premium';
    const price = requiredTier === 'pro' ? '$4.99/month' : '$39.00/year';
    
    addNotification({
      type: 'warning',
      message: `${featureName} requires SleepCalc ${tierName} (${price}). Upgrade to unlock this feature!`,
      duration: 6000,
      action: {
        label: 'Upgrade Now',
        onClick: () => {
          window.location.href = '/pricing';
        }
      }
    });
  }, [addNotification]);

  /**
   * Get feature access message
   */
  const getFeatureAccessMessage = useCallback((featureTier: 'pro' | 'premium'): string => {
    if (canUseFeature(featureTier)) {
      return 'Feature available';
    }

    const currentPlanLevel = currentPlan === 'free' ? 0 : currentPlan === 'pro' ? 1 : 2;
    const requiredLevel = featureTier === 'pro' ? 1 : 2;

    if (currentPlanLevel < requiredLevel) {
      const tierName = featureTier === 'pro' ? 'Pro' : 'Premium';
      const price = featureTier === 'pro' ? '$4.99/month' : '$39.00/year';
      return `Requires SleepCalc ${tierName} (${price})`;
    }

    return 'Feature not available';
  }, [canUseFeature, currentPlan]);

  /**
   * Get plan display name
   */
  const getPlanDisplayName = useCallback((plan: SubscriptionPlan = currentPlan): string => {
    switch (plan) {
      case 'free':
        return 'Free';
      case 'pro':
        return 'Pro';
      case 'premium':
        return 'Premium';
      default:
        return 'Unknown';
    }
  }, [currentPlan]);

  /**
   * Get plan price
   */
  const getPlanPrice = useCallback((plan: SubscriptionPlan): string => {
    switch (plan) {
      case 'free':
        return 'Free';
      case 'pro':
        return '$4.99/month';
      case 'premium':
        return '$39.00/year';
      default:
        return 'Unknown';
    }
  }, []);

  /**
   * Get plan features
   */
  const getPlanFeatures = useCallback((plan: SubscriptionPlan): string[] => {
    const baseFeatures = [
      'Basic sleep calculator',
      'Standard scenarios',
      'Moon phase display',
      'Basic tips and recommendations'
    ];

    const proFeatures = [
      ...baseFeatures,
      'Advanced AI assistant',
      'Sleep analytics dashboard',
      'Custom sleep plans',
      'Device integrations',
      'Smart notifications',
      'Priority support'
    ];

    const premiumFeatures = [
      ...proFeatures,
      'Advanced moon tracking',
      'Sleep coaching sessions',
      'Custom scenarios',
      'Export data',
      'API access',
      'White-label options'
    ];

    switch (plan) {
      case 'free':
        return baseFeatures;
      case 'pro':
        return proFeatures;
      case 'premium':
        return premiumFeatures;
      default:
        return [];
    }
  }, []);

  /**
   * Refresh access data from server
   */
  const refreshAccess = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await refreshUserAccess();
      setAccessData(data);
    } catch (error) {
      console.error('刷新用户权限失败:', error);
    } finally {
      setIsLoading(false);
    }
  }, [refreshUserAccess]);

  /**
   * Sync access data to local store
   */
  const syncAccess = useCallback(async () => {
    try {
      await syncUserAccess();
      await refreshAccess();
    } catch (error) {
      console.error('同步用户权限失败:', error);
    }
  }, [syncUserAccess, refreshAccess]);

  return {
    isProUser,
    isPremiumUser,
    hasProAccess,
    hasPremiumAccess,
    currentPlan,
    
    // Loading state
    isLoading,
    
    // User data
    userId,
    accessData,
    
    // Feature access checks
    canUseFeature,
    requiresUpgrade,
    hasSpecificFeature,
    
    // UI helpers
    showUpgradePrompt,
    getFeatureAccessMessage,
    
    // Plan information
    getPlanDisplayName,
    getPlanPrice,
    getPlanFeatures,
    
    // Actions
    refreshAccess,
    syncAccess
  };
};